'use client';

import { ReactNode } from 'react';
import { css, stack } from '@bts-web/utils-style-engine';
import {
  AssetImage,
  ButtonBase,
  HyperLinkButtonBase,
} from '../../../../common';
import { PoweredBy } from '../../../Reusable/PoweredBy/PoweredBy';
import { stepContainerStyles } from './finishTradeStepStyles';

const titlesContainerStyles = css({
  margin: 'auto 0',
  gap: 'extra_large',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  pb: {
    base: '0',
    lg: 'medium',
  },
});

const assetLogoCircleStyles = css({
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '100%',
  p: {
    base: 'extra_small_2',
    lg: 'small',
  },
  // vertical margin matches the box-shadow blur radius below, reserving layout
  // space so the shadow halo isn't clipped by the modal's overflow:hidden
  // ancestor when the column is vertically compressed (e.g. LBBW's extra line)
  marginTop: '18px',
  backgroundColor: 'neutrals.fill_quinary',
  boxShadow: `0px 0px 18px 0px rgba(0, 0, 0, 0.25)`,
});

const mainMessageStyles = css({
  fontSize: 'headline.large_semi_bold',
  fontWeight: 'headline.large_semi_bold',
  lineHeight: 'headline.large_semi_bold',
  letterSpacing: 'headline.large_semi_bold',
  textAlign: 'center',
});

const subtitleStyles = css({
  textAlign: 'center',
  color: 'neutrals.text_primary',
  fontSize: 'body.medium',
  fontWeight: 'body.medium',
  letterSpacing: 'body.medium',
  lineHeight: 'body.medium',
});

const ctaSectionStyles = css({
  mt: 'auto',
});

const ctaButtonsStyles = stack({
  gap: 'small',
  alignItems: 'center',
  mx: 'auto',
  width: '100%',
  maxWidth: '390px',
});

export interface FinishTradeBodyProps {
  assetLogoUrl: string;
  assetName: string;
  title: ReactNode;
  subtitle?: ReactNode;
  statusContent?: ReactNode;
  statusTitleIcon?: ReactNode;
  poweredByText?: string;
  primaryButton?: {
    text: string;
    url: string;
    replace?: boolean;
  };
  secondaryButton?: {
    text: string;
    url: string;
  };
  primaryButtonText?: string;
  onPrimaryClick?: () => void;
  isBpfs: boolean;
}

export const FinishTradeBody = ({
  assetLogoUrl,
  assetName,
  title,
  subtitle,
  statusContent,
  statusTitleIcon,
  poweredByText,
  primaryButton,
  secondaryButton,
  primaryButtonText,
  onPrimaryClick,
  isBpfs,
}: FinishTradeBodyProps) => {
  const primaryButtonUrl = primaryButton?.url ?? '';

  return (
    <div className={stepContainerStyles}>
      <div className={titlesContainerStyles}>
        {assetLogoUrl && (
          <div className={assetLogoCircleStyles}>
            <AssetImage
              testId="trade-asset-logo"
              src={assetLogoUrl}
              width={48}
              height={48}
              alt={assetName}
              aria-label="asset logo"
              priority
            />
          </div>
        )}

        {statusTitleIcon}

        <h1
          data-testid="finish-trade-main-message"
          className={mainMessageStyles}
        >
          {title}
        </h1>

        {subtitle && <div className={subtitleStyles}>{subtitle}</div>}
      </div>

      {statusContent}

      <div className={ctaSectionStyles}>
        <div className={css({ mt: { base: 0, lg: 'large' } })}>
          <PoweredBy centered value={poweredByText} showLogo={!isBpfs} />
        </div>

        <div className={ctaButtonsStyles}>
          {primaryButton && (
            <>
              {onPrimaryClick ? (
                <ButtonBase
                  data-testid="primary-action-button"
                  visual="primary"
                  size="large"
                  fullWidth
                  onClick={onPrimaryClick}
                >
                  {primaryButtonText}
                </ButtonBase>
              ) : (
                <HyperLinkButtonBase
                  to={primaryButtonUrl}
                  replace={primaryButton.replace}
                  data-testid="primary-action-button"
                  visual="primary"
                  size="large"
                  fullWidth
                >
                  {primaryButtonText}
                </HyperLinkButtonBase>
              )}
            </>
          )}

          {secondaryButton && (
            <HyperLinkButtonBase
              to={secondaryButton.url}
              data-testid="secondary-action-button"
              visual="secondary"
              size="large"
              fullWidth
            >
              {secondaryButton.text}
            </HyperLinkButtonBase>
          )}
        </div>
      </div>
    </div>
  );
};
